import { useMemo } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { isPlatformAdmin } from '@/lib/platform-admin'
import { useSubscriptions } from '@/hooks/use-billing-subscriptions'
import type { ShopQuota } from '@/requests/billing'

export type SuggestedUpgrade = 'PLUS' | 'PRO' | null

function resolveSuggestedUpgrade(plan: string): SuggestedUpgrade {
  if (plan === 'FREE') return 'PLUS'
  if (plan === 'PLUS') return 'PRO'
  return null
}

export function useShopCreationQuota() {
  const user = useAuthStore((state) => state.auth.user)
  const platformAdmin = isPlatformAdmin(user)
  const { data, isLoading, isFetching, refetch } = useSubscriptions()

  const plan = data?.entitlements.plan ?? user?.role ?? 'FREE'
  const shop_quota: ShopQuota = data?.shop_quota ?? {
    used: 0,
    max_shops: plan === 'FREE' ? 1 : plan === 'PLUS' ? 2 : null,
    can_create: platformAdmin,
  }

  const needsUpgrade = useMemo(() => {
    if (platformAdmin) return false
    return !shop_quota.can_create
  }, [platformAdmin, shop_quota.can_create])

  const suggestedUpgrade = useMemo((): SuggestedUpgrade => {
    if (!needsUpgrade) return null
    return resolveSuggestedUpgrade(plan)
  }, [needsUpgrade, plan])

  return {
    plan,
    shop_quota,
    isPlatformAdmin: platformAdmin,
    needsUpgrade,
    suggestedUpgrade,
    isLoading: isLoading || isFetching,
    refetch,
  }
}
